<!--
  Componente QRCodeGenerator - Gera QR codes personalizados com logo do Avalize

  Exemplo de uso:
  <QRCodeGenerator
    :url="linkDaPesquisa"
    title="Pesquisa de Satisfação"
    :size="250"
    :logoSize="40"
  />
-->
<template>
  <div class="qrcode-container">
    <div class="qrcode-header" v-if="showTitle">
      <h3>{{ title || 'QR Code da Pesquisa' }}</h3>
      <p class="qrcode-subtitle">{{ subtitle || 'Escaneie para acessar a pesquisa' }}</p>
    </div>

    <div class="qrcode-display" ref="qrcode"></div>

    <div class="qrcode-actions" v-if="showActions">
      <v-btn
        color="#3BA8A2"
        dark
        small
        @click="downloadQRCode"
        :disabled="!qrCodeGenerated"
      >
        <v-icon left>mdi-download</v-icon>
        Baixar QR Code
      </v-btn>
    </div>

    <div class="qrcode-url" v-if="showURL">
      <small>URL: {{ url }}</small>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'QRCodeGenerator',
  props: {
    url: {
      type: String,
      required: true
    },
    size: {
      type: Number,
      default: 200
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showURL: {
      type: Boolean,
      default: true
    },
    color: {
      type: String,
      default: '#000000'
    },
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    margin: {
      type: Number,
      default: 4
    },
    logoUrl: {
      type: String,
      default: require('@/assets/logo.png') // Caminho para a logo do Avalize
    },
    logoSize: {
      type: Number,
      default: 60 // Aumentado para melhor qualidade visual
    },
    showLogo: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      qrCodeGenerated: false,
      qrCodeDataURL: ''
    }
  },
  mounted() {
    this.generateQRCode()
  },
  watch: {
    url() {
      this.generateQRCode()
    }
  },
  methods: {
    async generateQRCode() {
      try {
        if (!this.url) return

        const options = {
          width: this.size,
          margin: this.margin,
          color: {
            dark: this.color,
            light: this.backgroundColor
          }
        }

        // Gerar QR code em canvas
        const canvas = document.createElement('canvas')
        await QRCode.toCanvas(canvas, this.url, options)

        if (this.showLogo && this.logoUrl) {
          // Adicionar logo no centro
          await this.addLogoToCanvas(canvas)
        }

        this.qrCodeDataURL = canvas.toDataURL()

        // Limpar conteúdo anterior
        this.$refs.qrcode.innerHTML = ''

        // Criar elemento img para exibir o QR Code
        const img = document.createElement('img')
        img.src = this.qrCodeDataURL
        img.alt = 'QR Code da Pesquisa'
        img.style.maxWidth = '100%'
        img.style.height = 'auto'

        this.$refs.qrcode.appendChild(img)
        this.qrCodeGenerated = true

        this.$emit('qr-generated', this.qrCodeDataURL)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao gerar QR Code:', error)
        this.$emit('qr-error', error)
      }
    },

    async addLogoToCanvas(canvas) {
      return new Promise((resolve) => {
        const ctx = canvas.getContext('2d')
        const img = new Image()

        img.onload = () => {
          try {
            // Habilitar suavização para melhor qualidade
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'

            // Calcular posição e tamanho da logo
            const logoSize = this.logoSize
            const x = (canvas.width - logoSize) / 2
            const y = (canvas.height - logoSize) / 2

            // Criar fundo QUADRADO branco para a logo
            const padding = 8
            const backgroundSize = logoSize + padding * 2
            const bgX = (canvas.width - backgroundSize) / 2
            const bgY = (canvas.height - backgroundSize) / 2

            // Desenhar fundo quadrado branco
            ctx.fillStyle = this.backgroundColor
            ctx.fillRect(bgX, bgY, backgroundSize, backgroundSize)

            // Desenhar a logo com melhor qualidade
            ctx.drawImage(img, x, y, logoSize, logoSize)

            resolve()
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Erro ao adicionar logo ao QR Code:', error)
            resolve() // Continue sem a logo se houver erro
          }
        }

        img.onerror = () => {
          // eslint-disable-next-line no-console
          console.warn('Não foi possível carregar a logo:', this.logoUrl)
          resolve() // Continue sem a logo se não conseguir carregar
        }

        // Tentar carregar a logo do Avalize com melhor qualidade
        img.crossOrigin = 'anonymous'
        img.src = this.logoUrl
      })
    },

    downloadQRCode() {
      if (!this.qrCodeDataURL) return

      const link = document.createElement('a')
      link.href = this.qrCodeDataURL
      link.download = `qrcode-pesquisa-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$emit('qr-downloaded')
    }
  }
}
</script>

<style scoped>
.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 10px;
  margin: 10px 0;
}

.qrcode-header {
  text-align: center;
  margin-bottom: 20px;
}

.qrcode-header h3 {
  color: #424f4e;
  margin: 0 0 8px 0;
  font-weight: bold;
}

.qrcode-subtitle {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.qrcode-display {
  margin: 20px 0;
  padding: 15px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.qrcode-actions {
  margin: 15px 0;
}

.qrcode-url {
  margin-top: 15px;
  text-align: center;
  color: #666;
  font-size: 12px;
  word-break: break-all;
  max-width: 100%;
}

@media (max-width: 600px) {
  .qrcode-container {
    padding: 15px;
  }

  .qrcode-actions .v-btn {
    margin: 5px 0 !important;
    width: 100%;
  }
}
</style>